import { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "@/content";
import { WhatsAppGlyph, whatsappLink } from "@/components/WhatsApp";

const NAV = [
  { id: "about", label: "المشروع" },
  { id: "location", label: "الموقع" },
  { id: "specs", label: "المواصفات" },
  { id: "warranties", label: "الضمانات" },
  { id: "models", label: "النماذج" },
  { id: "gallery", label: "المعرض" },
  { id: "faq", label: "أسئلة" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "shadow-[0_2px_24px_-12px_rgba(26,32,22,0.25)]" : ""
      }`}
      data-testid="navbar"
    >
      {/* Top utility bar */}
      <div
        className={`hidden lg:block transition-all duration-500 overflow-hidden ${
          scrolled ? "max-h-0 opacity-0 pointer-events-none" : "max-h-10 opacity-100"
        } bg-brand-olive-deep/70 backdrop-blur-sm border-b border-brand-cream/10`}
      >
        <div className="max-w-7xl mx-auto px-10 h-9 flex items-center justify-between gap-6 text-[11px] text-brand-cream/85" >
          <div className="flex items-center gap-6">
            <a href={BRAND.telUrl} className="inline-flex items-center gap-2 hover:text-brand-accent transition-colors" data-testid="navbar-top-phone">
              <Phone size={11} />
              <span dir="ltr">{BRAND.phoneDisplay}</span>
            </a>
            <a
              href={whatsappLink(null, "navbar-top")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 hover:text-brand-accent transition-colors"
              data-testid="navbar-top-whatsapp"
            >
              <WhatsAppGlyph size={11} />
              <span>تواصل واتساب</span>
            </a>
          </div>
          <div className="flex items-center gap-3 text-brand-cream/60">
            <span>{BRAND.cityAr} — السعودية</span>
            <span className="text-brand-accent">•</span>
            <span>مبيعات المرحلة الأولى</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div
        className={`transition-all duration-500 ${
          scrolled
            ? "bg-brand-cream/95 backdrop-blur-md"
            : "bg-gradient-to-b from-brand-olive-deep/55 via-brand-olive-deep/25 to-transparent"
        }`}
      >
        <div
          dir="ltr"
          className={`relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-6 transition-all duration-300 ${
            scrolled ? "h-[72px]" : "h-24"
          }`}
        >
          {/* Right: Logo */}
          <div className="absolute right-24 top-1/2 flex w-32 -translate-y-1/2 items-center justify-end overflow-visible sm:right-6 lg:static lg:order-3 lg:w-auto lg:translate-y-0" dir="rtl">
            <button
              onClick={() => scrollTo("hero")}
              className="flex w-full items-center justify-end overflow-visible transition-all duration-300 lg:w-auto"
              data-testid="navbar-logo-btn"
              aria-label="الرئيسية"
            >
              <span className="block lg:hidden">
                <Logo size="md" variant={scrolled ? "cream" : "dark"} />
              </span>
              <span className="hidden lg:block">
                <Logo size={scrolled ? "md" : "lg"} variant={scrolled ? "cream" : "dark"} />
              </span>
            </button>
          </div>

          {/* Center: Nav */}
          <nav className="lg:order-2 hidden lg:flex items-center justify-center gap-8" aria-label="القائمة الرئيسية" dir="rtl">
            {NAV.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`relative text-[13px] font-medium tracking-wide transition-colors duration-300 hover:text-brand-accent group ${
                  scrolled ? "text-brand-ink" : "text-brand-cream"
                }`}
                data-testid={`nav-${item.id}-btn`}
              >
                {item.label}
                <span className="absolute -bottom-2 inset-x-0 mx-auto w-0 h-px bg-brand-accent group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </nav>

          {/* Left: CTA + mobile toggle */}
          <div className="absolute left-6 top-1/2 -translate-y-1/2 lg:static lg:order-1 lg:translate-y-0 flex items-center justify-start gap-3" dir="rtl">
            <button
              onClick={() => scrollTo("form")}
              className={`hidden lg:inline-flex items-center gap-2 px-6 py-2.5 text-[13px] font-medium rounded-xl transition-all duration-300 hover:-translate-y-0.5 ${
                scrolled
                  ? "bg-brand-olive text-brand-cream hover:bg-brand-olive-dark"
                  : "bg-brand-accent text-brand-ink hover:bg-brand-accent-dark"
              }`}
              data-testid="navbar-cta-btn"
            >
              سجّل اهتمامك
              <span aria-hidden="true">←</span>
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className={`lg:hidden p-2 ${scrolled ? "text-brand-ink" : "text-brand-cream"}`}
              aria-label="القائمة"
              data-testid="navbar-mobile-toggle"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-brand-cream border-t border-brand-border"
          data-testid="navbar-mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="text-right text-brand-ink py-2 border-b border-brand-border/50"
                data-testid={`nav-mobile-${item.id}-btn`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("form")}
              className="mt-2 bg-brand-olive text-brand-cream py-3 text-sm font-medium rounded-xl"
              data-testid="navbar-mobile-cta-btn"
            >
              سجّل اهتمامك
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

